import { yupResolver } from "@hookform/resolvers/yup";
import { designTokens } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputRadio } from "@itandi/itandi-bb-ui/InputRadio";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import type {
	CompanySetting,
	CompanySettingEdit
} from "~/models/CompanySetting";
import { validationSchemaCompanySetting } from "~/models/CompanySetting";
import { isSuccess } from "~/utils/api/api_handler";
import { update } from "~/utils/api/CompanySetting";

type Props = {
	companySetting: CompanySetting;
};
export function CompanySettingForm({ companySetting }: Props) {
	const { notify } = useNotification();
	const { isOpen, handleOpen, handleClose } = useOpenHandler();
	const { register, getValues, control, formState, handleSubmit } =
		useForm<CompanySettingEdit>({
			defaultValues: companySetting,
			mode: "onBlur",
			resolver: yupResolver(validationSchemaCompanySetting)
		});
	const { errors } = formState;
	const autoFillEstimateAddressType = useWatch({
		control,
		name: "autoFillEstimateAddressType"
	});
	const defaultInspectionBy = useWatch({
		control,
		name: "defaultInspectionBy"
	});
	const onClickSubmit = React.useCallback(() => {
		handleSubmit(() => {
			handleOpen();
		})();
	}, [handleSubmit, handleOpen]);
	const handleAccept = React.useCallback(async () => {
		const values = getValues();
		const response = await update(values);
		if (isSuccess(response)) {
			notify({ content: "保存しました。", variant: "success" });
			handleClose();
			return;
		}
		notify({ content: "保存に失敗しました。", variant: "error" });
	}, [getValues, handleClose, notify]);
	return (
		<>
			<FormGroup maxW="32rem" variant="vertical">
				<FormControl chip="必須" isInvalid={!!errors.name} label="会社名">
					<Input {...register("name")} placeholder="例）サンプル株式会社" />
					<FormControlText error={errors.name?.message} />
				</FormControl>
				<FormControl label="会社代表者氏名">
					<Flex gap={designTokens["spacing/xs"]}>
						<Input {...register("ceoLastName")} placeholder="例）賃貸" />
						<Input {...register("ceoFirstName")} placeholder="例）太郎" />
					</Flex>
				</FormControl>
				<FormControl label="居住用管理戸数">
					<Input {...register("managedUnitNumber")} placeholder="例）500" />
				</FormControl>
				<FormControl
					chip="必須"
					isInvalid={!!errors.defaultInspectionBy}
					label="発注書請書の宛名を補完するか"
				>
					<Flex gap={designTokens["spacing/s"]}>
						<InputRadio
							checked={autoFillEstimateAddressType === "none"}
							{...register("autoFillEstimateAddressType")}
							label="補完しない"
							value="none"
						/>
						<InputRadio
							checked={autoFillEstimateAddressType === "company_name"}
							{...register("autoFillEstimateAddressType")}
							label="会社名で補完する"
							value="company_name"
						/>
					</Flex>
					<FormControlText
						error={errors.autoFillEstimateAddressType?.message}
					/>
				</FormControl>
				<FormControl
					chip="必須"
					isInvalid={!!errors.defaultInspectionBy}
					label="デフォルトの立会設定"
				>
					<Flex gap={designTokens["spacing/s"]}>
						<InputRadio
							checked={defaultInspectionBy === "vendor"}
							{...register("defaultInspectionBy")}
							label="施工会社"
							value="vendor"
						/>
						<InputRadio
							checked={defaultInspectionBy === "company"}
							label="管理会社"
							value="company"
							{...register("defaultInspectionBy")}
						/>
						<InputRadio
							checked={defaultInspectionBy === "none"}
							label="立ち会わない"
							value="none"
							{...register("defaultInspectionBy")}
						/>
					</Flex>
					<FormControlText error={errors.defaultInspectionBy?.message} />
				</FormControl>
			</FormGroup>
			<Button
				disabled={!formState.isDirty}
				marginTop={designTokens["spacing/m"]}
				onClick={onClickSubmit}
				variant="primary"
			>
				保存
			</Button>
			<ConfirmModal
				confirmType="save"
				handleAccept={handleAccept}
				handleCancel={handleClose}
				isOpen={isOpen}
			>
				保存してよろしいですか？
			</ConfirmModal>
		</>
	);
}
